import React, {
    useEffect,
    useState,
} from 'react';

import classNames from 'classnames';
import {
    Button,
    Heading,
    ToggleButton,
} from 'react-aria-components';

import {
    List,
    ListItem,
    Text,
    WDSModal,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';
import { CloseX } from 'wanderu-icons';

import styles from './Fare.scss';

import type { Detail } from '../types';
import type { TFunction } from 'i18next';

interface FareProps {
    code: string;
    details: Detail[];
    disabled?: boolean;
    farePrice: number;
    forceModalClose: boolean;
    i18nMoney: (amount: number) => { format(): string };
    onChange: (code: string, type: string) => void;
    selectedFarePrice: number;
    t: TFunction;
    type: string;
}

function Fare({
    code,
    details,
    disabled,
    farePrice,
    forceModalClose,
    i18nMoney,
    onChange,
    selectedFarePrice,
    t,
    type,
}: FareProps) {
    const [showMoreModal, setShowMoreModal] = useState(false);

    useEffect(() => {
        if (forceModalClose && showMoreModal) {
            setShowMoreModal(false);
        }
    }, [forceModalClose, showMoreModal]);

    const onClick = () => {
        setShowMoreModal(true);
    };
    const price = farePrice - selectedFarePrice;
    const prefix = price >= 0
        ? '+'
        : '';

    return (
        <>
            <ToggleButton
                className={styles.fareContainer}
                id={code}
                isDisabled={disabled}
                onPress={() => onChange(code, type)}
            >
                <div
                    className={classNames(styles.fareLabel, {
                        [styles.disabled]: disabled,
                    })}
                >
                    <Text
                        className={classNames(styles.label, {
                            [styles.disabled]: disabled,
                        })}
                        component="h5"
                        bold
                    >
                        {t(type)}
                    </Text>
                    <Text
                        className={styles.priceDiff}
                        component="h6"
                        variant="text-xl"
                        bold
                    >
                        {disabled
                            ? t('sold-out')
                            : `${prefix}${i18nMoney(price).format()}`}
                    </Text>

                    {details?.length && <>
                        {details.slice(0, 2).map(({ key }) => (
                            <Text
                                className={styles.detail}
                                key={key}
                                variant="text-xs"
                            >{t(key)}</Text>
                        ))}
                        <Button className={styles.more} onPress={onClick}>{t<string>('more')}</Button>
                    </>}
                </div>
            </ToggleButton>

            {showMoreModal && (
                <WDSModal
                    classes={{
                        dialog: styles.dialog,
                        modal: styles.modal,
                        modalOverlay: styles.modalOverlay,
                    }}
                    isOpen={showMoreModal}
                    onOpenChange={setShowMoreModal}
                    isDismissable
                >
                    <div className={styles.dialogHeaderContainer}>
                        <Heading className={styles.dialogHeader} slot="title">
                            {t<string>(type)}
                        </Heading>
                        <Button
                            className={styles.closeButton}
                            onPress={() => setShowMoreModal(false)}
                        >
                            <CloseX />
                        </Button>
                    </div>

                    <List className={styles.detailList}>
                        {details.map(({ key }) => (
                            <ListItem className={styles.detailListItem} key={key}>
                                {t(key)}
                            </ListItem>
                        ))}
                    </List>
                </WDSModal>
            )}
        </>
    );
}

export default localize(Fare, ['i18nMoney', 't'], 'baseApp');
